import { useEffect, useCallback } from 'react';
import { startClientModeSession, sendClientMessageStream } from '../services/api';

let clientModeStartInFlightKey = null;
let clientModeStartedActivationKey = null;

function useClientMode({
  isClientMode,
  sessionId,
  sessionEnded,
  loading,
  conversationLength,
  counsellorMode,
  clientModeCounsellorMode,
  btcActivationRequest,
  btcCueFlowActive,
  onBtcCueFlowActiveChange,
  onConversationStart,
  onSessionRegistered,
  onSystemMessagesUpdate,
  onStageMetricsUpdate,
  onSessionEnd,
  notify,
  setLoading,
  setConversation,
  setSessionEnded,
  setEndReason,
}) {
  const isConsentPending = isClientMode && !!sessionId && conversationLength === 1 && !sessionEnded && !loading;
  const isBtcFirstUserTurnReady = isClientMode && !!sessionId && conversationLength >= 3 && !sessionEnded && !loading && !isConsentPending;

  const finalizeStreamedCounsellorReply = useCallback(() => {
    setConversation((prev) => {
      const updated = [...prev];
      const last = updated[updated.length - 1];
      updated[updated.length - 1] = { speaker: 'counsellor', text: last.text };
      return updated;
    });
  }, [setConversation]);

  const appendCounsellorStreamToken = useCallback((token) => {
    setConversation((prev) => {
      const updated = [...prev];
      const last = updated[updated.length - 1];
      updated[updated.length - 1] = { ...last, text: last.text + token };
      return updated;
    });
  }, [setConversation]);

  const handleClientModeSessionEnd = useCallback((data) => {
    if (data.session_ended) {
      setSessionEnded(true);
      setEndReason(data.end_reason || 'Session ended');
      if (onSessionEnd) {
        onSessionEnd(sessionId);
      }
    }
  }, [onSessionEnd, sessionId, setEndReason, setSessionEnded]);

  const handleActivateCounsellor = useCallback(async (requestedCounsellorMode = counsellorMode) => {
    const activationKey = `${btcActivationRequest}:${requestedCounsellorMode}`;
    if (
      loading ||
      sessionId ||
      !requestedCounsellorMode ||
      clientModeStartInFlightKey === activationKey ||
      clientModeStartedActivationKey === activationKey
    ) {
      return;
    }
    clientModeStartInFlightKey = activationKey;

    setLoading(true);
    try {
      const sessionData = await startClientModeSession(requestedCounsellorMode);
      clientModeStartedActivationKey = activationKey;
      setConversation(sessionData.initial_message ? [sessionData.initial_message] : []);
      onSessionRegistered?.(sessionData.session_id);
    } catch (err) {
      console.error('Failed to activate counsellor:', err);
      if (clientModeStartedActivationKey === activationKey) {
        clientModeStartedActivationKey = null;
      }
      const apiMsg = err?.response?.data?.error;
      notify(apiMsg || 'Failed to start session. Please check backend connection.');
    } finally {
      setLoading(false);
      if (clientModeStartInFlightKey === activationKey) {
        clientModeStartInFlightKey = null;
      }
    }
  }, [
    btcActivationRequest,
    counsellorMode,
    loading,
    notify,
    onSessionRegistered,
    sessionId,
    setConversation,
    setLoading,
  ]);

  useEffect(() => {
    if (!isClientMode || btcActivationRequest === 0 || sessionId || sessionEnded || !clientModeCounsellorMode) {
      return;
    }
    handleActivateCounsellor(clientModeCounsellorMode);
  }, [
    btcActivationRequest,
    clientModeCounsellorMode,
    handleActivateCounsellor,
    isClientMode,
    sessionEnded,
    sessionId,
  ]);

  const handleClientModeSend = useCallback(async (messageToSend) => {
    if (btcCueFlowActive) {
      onBtcCueFlowActiveChange?.(false);
    }

    setConversation((prev) => [
      ...prev,
      { speaker: 'client', text: messageToSend },
      { speaker: 'counsellor', text: '', isStreaming: true },
    ]);

    const { promise } = sendClientMessageStream(
      messageToSend,
      appendCounsellorStreamToken,
      (data) => {
        finalizeStreamedCounsellorReply();

        if (onSystemMessagesUpdate) {
          const counsellorMsgs = data.counselor_message?.system_messages || [];
          const clientMsgs = data.smoker_response?.system_messages || [];
          onSystemMessagesUpdate(
            counsellorMsgs,
            clientMsgs,
            data.stage_metrics || null,
            data.working_alliance_series || [],
            data.all_tasks_series || []
          );
        }
        if (onStageMetricsUpdate && data.stage_metrics) {
          onStageMetricsUpdate(data.stage_metrics);
        }
        handleClientModeSessionEnd(data);
        setLoading(false);
      },
      (errorMsg) => {
        console.error('Client mode stream error:', errorMsg);
        notify('Failed to send message. Please try again.');
        setConversation((prev) => prev.filter((message) => !message.isStreaming));
        setLoading(false);
      }
    );

    await promise;
  }, [
    appendCounsellorStreamToken,
    btcCueFlowActive,
    finalizeStreamedCounsellorReply,
    handleClientModeSessionEnd,
    notify,
    onBtcCueFlowActiveChange,
    onStageMetricsUpdate,
    onSystemMessagesUpdate,
    setConversation,
    setLoading,
  ]);

  const handleConsentChoice = useCallback(async (choice) => {
    if (loading || sessionEnded || !sessionId) return;
    onConversationStart?.();
    setLoading(true);
    setConversation((prev) => [
      ...prev,
      { speaker: 'client', text: choice },
      { speaker: 'counsellor', text: '', isStreaming: true },
    ]);

    const { promise } = sendClientMessageStream(
      choice,
      appendCounsellorStreamToken,
      (data) => {
        finalizeStreamedCounsellorReply();
        onSystemMessagesUpdate?.(
          data.counselor_message?.system_messages || [],
          [],
          data.stage_metrics || null,
          data.working_alliance_series || [],
          data.all_tasks_series || []
        );
        if (onStageMetricsUpdate && data.stage_metrics) {
          onStageMetricsUpdate(data.stage_metrics);
        }
        handleClientModeSessionEnd(data);
        setLoading(false);
      },
      (errorMsg) => {
        console.error('Consent choice error:', errorMsg);
        notify('Failed to send. Please try again.');
        setConversation((prev) => prev.filter((message) => !message.isStreaming));
        setLoading(false);
      }
    );

    await promise;
  }, [
    appendCounsellorStreamToken,
    finalizeStreamedCounsellorReply,
    handleClientModeSessionEnd,
    loading,
    notify,
    onConversationStart,
    onStageMetricsUpdate,
    onSystemMessagesUpdate,
    sessionEnded,
    sessionId,
    setConversation,
    setLoading,
  ]);

  return {
    isConsentPending,
    isBtcFirstUserTurnReady,
    handleActivateCounsellor,
    handleClientModeSend,
    handleConsentChoice,
  };
}

export default useClientMode;
